import { z } from "zod";
import { PaginationSchema } from "../../pagination/response/PaginationResponse";

/**
 * Skema untuk relasi 'groups' yang bisa disertakan secara opsional.
 * Mendefinisikan skema terpisah untuk relasi membuat kode lebih bersih
 * dan lebih mudah dikelola.
 */
const GroupSchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    // Anda bisa menambahkan field lain dari tabel group di sini jika dibutuhkan.
});

/**
 * Skema utama untuk satu objek Company.
 * Ini mendefinisikan semua field yang diharapkan dari API untuk satu perusahaan,
 * termasuk URL untuk gambar yang sudah diunggah dan relasi opsional.
 */
export const CompanySchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    phone: z.string().nullable(),
    email: z.string().email("Invalid email format received from API").nullable(),
    address: z.string().nullable(),
    url: z.string().nullable(),
    color: z.string().nullable(),
    logo_url: z.string().nullable(),
    cover_image_url: z.string().nullable(),
    created_at: z.string().datetime("Invalid datetime format for created_at"),
    updated_at: z.string().datetime("Invalid datetime format for updated_at"),
    favicon_url: z.string().nullable(),

    // Custom additional fields (object, optional)
    additional: z.nullable(z.record(z.any())).optional(),

    // Partner Integration fields
    partner_status: z.enum(["active", "inactive", "suspended"]).nullable().optional(),
    partner_key: z.string().nullable().optional(),
    partner_permissions: z.array(z.string()).nullable().optional(),
    partner_expires_at: z.string().nullable().optional(),

    // Relasi 'groups' bersifat opsional karena tergantung pada parameter 'include'
    // yang dikirim dalam request API.
    groups: z.array(GroupSchema).optional(),
});

/**
 * Skema untuk keseluruhan respons dari endpoint GET /company.
 * Ini mencakup wrapper standar (success, message, code), array data
 * yang divalidasi dengan CompanySchema, dan objek paginasi opsional.
 */
export const CompanyResponseSchema = z.object({
    success: z.boolean(),
    message: z.string(),
    code: z.number().int(),
    data: z.array(CompanySchema),
    pagination: z.nullable(PaginationSchema).optional(),
});

// Mengekspor tipe data TypeScript yang di-infer dari skema Zod.
// Ini sangat berguna untuk memastikan type safety di seluruh aplikasi
// saat bekerja dengan data perusahaan.
export type CompanyResponse = z.infer<typeof CompanyResponseSchema>;
export type CompanyData = z.infer<typeof CompanySchema>;